import React, { useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useRouter } from 'next/router';

import { useAxios } from '../../lib/custom-axios/CustomAxios';
import { keepToken } from '../../lib/token/token';
import { loginPath } from '../../lib/api/Api';
import { UserContext, UserType } from '../../contexts/user/UserContext';
import SuccessAlert from '../../lib/alerts/SuccessAlert';
import { ModalPortal } from '../../lib/modal/ModalPortal';
import Link from 'next/link';
import createToast from '../../lib/toast/CreateToast';
import { ToastBg } from '../../lib/toast/NotificationToast';
import Password from '../../lib/form/Password';
import { FormProvider, useForm } from 'react-hook-form';
import { LocalStorageContext } from '../../contexts/storage/LocalStorageContext';

interface ISigninProps {
    isModal?: boolean;
    onSuccess?: () => void;
    email: string;
    goBack: () => void;
}

const SigninEmail: React.FC<ISigninProps> = ({isModal, email, onSuccess, goBack}) => {
    const {loading, fetch, data, error} = useAxios();
    const {getMe} = useContext(UserContext);
    const {updateStorage} = useContext(LocalStorageContext);
    const router = useRouter();
    const [showModal, setShowModal] = useState(false);
    const {message} = router.query as { message: string };
    const methods = useForm({
        shouldUseNativeValidation: false,
        mode: 'onBlur',
        reValidateMode: 'onChange',
        resolver: undefined,
        context: undefined,
        criteriaMode: 'all',
        shouldFocusError: true,
        shouldUnregister: true,
        delayError: 100
    });

    useEffect(() => {
        if (!loading && data) {
            keepToken(JSON.stringify(data));
            getMe?.(onSuccess);
        } else if (!loading && error) {
            if (error.message === 'wrongUserType') {
                setShowModal(true);
            } else {
                createToast({
                    bg: ToastBg.Warning,
                    heading: 'Error',
                    content: error.message
                });
            }
        }
    }, [loading]);

    const resetHandler = () => {
        router.push('/reset-password');
    };

    const onSubmitHandeler = (data: any) => {
        fetch(loginPath(UserType.CONSUMER), {
            body: data
        }, false, false);
    };

    return (
        <div className={!isModal ? 'card' : ''}>
            <div className="px-3 pt-3">
                {message === 'reset_password' && (
                    <div className="mb-4">
                        <SuccessAlert message="Your password has been reset!"/>
                    </div>
                )}
                <h2 style={{fontSize: '28px'}} className="title mb-4">Sign in using your email address</h2>
                <FormProvider {...methods}>
                    <Form onSubmit={methods.handleSubmit(onSubmitHandeler as any)}>
                        <Form.Group>
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type={'hidden'}
                                {...methods.register(`email`, {value: email})}
                            />
                            <div style={{border: '1px solid #D4D4D8', backgroundColor: '#F4F4F5'}}
                                 className="form-control d-flex justify-content-between align-items-start px-3 py-2 mb-4">
                                <span
                                    style={{maxWidth: 'calc(100% - 88px)'}}
                                    className="text-truncate">
                                    {email}
                                </span>
                                <Button
                                    className="link-button"
                                    variant="link"
                                    onClick={goBack}>
                                    Change
                                </Button>
                            </div>
                        </Form.Group>
                        <Password
                            label="Password"
                            error={methods?.getFieldState('password').error}
                            inputProps={{
                                className: `${methods?.getFieldState('password').error ? 'is-invalid' : ''}`,
                                ...methods.register('password', {
                                    required: true
                                })
                            }}
                        />
                        <Button
                            className="link-button"
                            variant="link"
                            onClick={resetHandler}>
                            Forgot your password?
                        </Button>
                        <Button
                            className="submitButton w-100 mt-3 mb-4"
                            type="submit"
                            disabled={loading || methods.formState.isSubmitting}>
                            Sign in
                        </Button>
                    </Form>
                </FormProvider>
            </div>

            <ModalPortal
                show={showModal}
                showFooter={false}
                onCloseHandler={() => setShowModal(false)}
                heading={(
                    <div className={'d-flex justify-content-between align-items-center'}>
                        <span>Warning</span>
                        <Button
                            className="link-button"
                            variant={'link'}
                            onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                    </div>
                )}>
                <div className="p-3 pt-0">{
                    <span>
                        Seems like you are trying to sign in to your dealer portal account, please go to <Link
                        legacyBehavior href={methods.getValues('email') ? `/dealer/signin?currentEmail=${encodeURIComponent(methods.getValues('email'))}` : '/dealer/signin'}><a>Dealer portal sign in</a></Link> page and try again.
                    </span>
                }</div>
            </ModalPortal>
        </div>
    );
};

export default SigninEmail;
